import { Camera, ChevronUp, Mic, MonitorUp } from "lucide-react";
import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  PopoverHeader,
  PopoverTitle,
  PopoverDescription,
  PopoverSeparator,
} from "../ui/popover";
import { useRoomController, useRoomState } from "../../room/RoomContext";
import { useTranslation } from "react-i18next";
import { useState, type ReactNode } from "react";
import type { MicrophoneProblem } from "../../room/state/media";
import type { MediaSource } from "../../room/media-settings";
import { dockPopoverProps, useDockCard } from "../stage/dock-anchor";

// ButtonGroup squares the settings end when the floating spectator badge is last; shared paint/ring and half-hover masks still need bespoke styles.
// In a dock the popover centres on the dock card; elsewhere the whole pill anchors it.
export function MediaControl({
  source,
  title,
  description,
  children,
  extra,
}: {
  source: MediaSource;
  title: string;
  description: string;
  children: ReactNode;
  extra?: ReactNode;
}) {
  const { t } = useTranslation();
  const controller = useRoomController();
  const card = useDockCard();
  const active = useRoomState((s) =>
    source === "screen"
      ? s.media.screenActive
      : source === "camera"
        ? s.media.cameraActive
        : s.media.microphoneActive,
  );
  const disabled = useRoomState((s) =>
    source === "screen"
      ? s.media.screenDisabled
      : source === "camera"
        ? s.media.cameraDisabled
        : s.media.microphoneDisabled,
  );
  const open = useRoomState((s) => s.media.mediaConfigOpen === source);
  const speaking = useRoomState((s) => source === "microphone" && s.media.microphoneSpeaking);
  const permissionPending = useRoomState((s) => s.media.microphonePermissionPending);
  const microphone = source === "microphone";
  const problem = useRoomState((s) => (microphone ? s.media.microphoneProblem : ""));
  const blocked = problem === "blocked";
  const screen = source === "screen";
  const prefix = microphone ? "voice" : source;
  const label = t(`${prefix}.${active ? "stop" : "setup"}`);
  const primaryTitle = t(active ? `${prefix}.stop` : screen ? "screen.share" : `common.${source}`);
  const buttonTitle = problem
    ? undefined
    : microphone && disabled
      ? t(permissionPending ? "voice.permissionPending" : "voice.permissionRequired")
      : primaryTitle;
  const settingsLabel = t(`${prefix}.adjust`);
  const PrimaryIcon = screen ? MonitorUp : microphone ? Mic : Camera;
  const pillId = screen ? "screenSharePill" : `${source}Pill`;
  const primaryId = `toggle${source[0].toUpperCase()}${source.slice(1)}`;
  return (
    <Popover open={open} onOpenChange={(value) => controller.setMediaConfigOpen(source, value)}>
      {card ? <PopoverAnchor virtualRef={card} /> : null}
      <MicrophoneProblemTooltip problem={problem} settingsOpen={open}>
        <PillAnchor dock={card !== null}>
          <TooltipTrigger
            asChild
            onFocus={(event) => {
              if (problem) event.preventDefault();
            }}
          >
            <div
              id={pillId}
              data-active={active}
              data-speaking={speaking}
              className="relative isolate flex h-(--dock-control-size) min-w-(--dock-control-size) shrink-0 items-center rounded-full p-0 before:absolute before:inset-0 before:-z-10 before:rounded-full before:bg-dock-pill before:ring-quality-good before:transition-colors before:duration-150 before:content-[''] data-[active=true]:before:bg-primary data-[active=true]:data-[speaking=true]:before:ring-2"
            >
              <Button
                id={primaryId}
                variant={null}
                size="icon"
                disabled={disabled}
                aria-label={label}
                aria-pressed={active}
                title={buttonTitle}
                onClick={() => void controller.toggleMedia(source)}
                className="relative grid size-(--dock-control-size) min-h-0 shrink-0 basis-(--dock-control-size) place-items-center rounded-full border-0 bg-transparent p-0 text-foreground shadow-none after:pointer-events-none after:absolute after:inset-0 after:rounded-l-full after:content-[''] hover:after:bg-white/[.11] disabled:cursor-not-allowed disabled:opacity-40 [&>svg]:relative [&>svg]:z-1 [&>svg]:size-4.5 [&>svg]:stroke-[2.15]"
              >
                <PrimaryIcon aria-hidden="true" />
                <span className="hidden">{primaryTitle}</span>
              </Button>
              <PopoverTrigger asChild>
                <Button
                  id={`${source}SettingsToggle`}
                  variant={null}
                  size="icon"
                  aria-label={settingsLabel}
                  title={problem ? undefined : settingsLabel}
                  disabled={blocked}
                  className="relative grid h-(--dock-control-size) min-h-0 w-7 shrink-0 basis-7 place-items-center rounded-full border-0 bg-transparent p-0 text-white shadow-none before:pointer-events-none before:absolute before:inset-y-2 before:left-0 before:z-2 before:w-px before:bg-dock-divider before:content-[''] after:pointer-events-none after:absolute after:inset-0 after:rounded-r-full after:content-[''] hover:after:bg-white/[.11] disabled:cursor-not-allowed disabled:opacity-40 [&>svg]:relative [&>svg]:z-1 [&>svg]:size-4 [&>svg]:stroke-[2.3] [&>svg]:transition-transform [&>svg]:duration-150 data-[state=open]:[&>svg]:rotate-180"
                >
                  <ChevronUp aria-hidden="true" />
                </Button>
              </PopoverTrigger>
              {extra}
            </div>
          </TooltipTrigger>
        </PillAnchor>
      </MicrophoneProblemTooltip>
      <PopoverContent
        id={`${source}ConfigPopover`}
        aria-labelledby={`${source}ConfigTitle`}
        aria-describedby={`${source}ConfigDescription`}
        {...dockPopoverProps}
        collisionPadding={8}
        className="grid max-h-[calc(100dvh-9rem)] w-120 max-w-[calc(100vw-1rem)] gap-6 overflow-y-auto"
      >
        <PopoverHeader>
          <PopoverTitle id={`${source}ConfigTitle`}>{title}</PopoverTitle>
          <PopoverDescription id={`${source}ConfigDescription`}>{description}</PopoverDescription>
        </PopoverHeader>
        <PopoverSeparator />
        {children}
      </PopoverContent>
    </Popover>
  );
}

function PillAnchor({ dock, children }: { dock: boolean; children: ReactNode }) {
  return dock ? children : <PopoverAnchor asChild>{children}</PopoverAnchor>;
}

const problemTipKeys = {
  blocked: "voice.blockedTip",
  "not-found": "voice.notFoundTip",
  "in-use": "voice.inUseTip",
  failed: "voice.failedTip",
} as const;

// Reset only the tooltip state when the cause changes, preserving button focus.
function MicrophoneProblemTooltip({
  problem,
  settingsOpen,
  children,
}: {
  problem: MicrophoneProblem;
  settingsOpen: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const [tip, setTip] = useState({ problem, dismissed: false, hovered: false });
  if (tip.problem !== problem) setTip({ problem, dismissed: false, hovered: false });
  const dismiss = () => setTip({ problem, dismissed: true, hovered: false });
  return (
    <Tooltip
      open={Boolean(problem) && !settingsOpen && (!tip.dismissed || tip.hovered)}
      onOpenChange={(value) =>
        setTip((previous) => ({ ...previous, hovered: previous.dismissed && value }))
      }
    >
      {children}
      {problem && (
        <TooltipContent
          id="microphoneProblemTip"
          side="top"
          // Clears the media row; it may cover the dock's top row, so clicks pass through to it.
          sideOffset={8}
          className="pointer-events-none max-w-64"
          onPointerDownOutside={dismiss}
          onEscapeKeyDown={dismiss}
        >
          {t(problemTipKeys[problem])}
        </TooltipContent>
      )}
    </Tooltip>
  );
}
